<script setup lang="ts">
  import { computed, onMounted, ref, watch } from "vue";
  import { useRoute, useRouter } from "vue-router";
  import { storeToRefs } from "pinia";
  import { Download, Loader2 } from "lucide-vue-next";
  import ThreePanePage from "@/templates/ThreePanePage.vue";
  import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
  import { Button } from "@/components/ui/button";
  import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
  import { Label } from "@/components/ui/label";
  import { Select } from "@/components/episto-ui/select";
  import ExportFormGeneral from "@/components/Survey/Exports/ExportFormGeneral.vue";
  import ExportFormIndividual from "@/components/Survey/Exports/ExportFormIndividual.vue";
  import ExportFormTables from "@/components/Survey/Exports/ExportFormTables.vue";
  import { useCurrentUserStore } from "@stores/currentUser";
  import { useSurveyStore } from "@stores/survey/survey";
  import { useSurveyTargetsStore } from "@stores/survey/surveyTargets";
  import { useSurveyLocalesStore } from "@stores/survey/surveyLocales";
  import { useResultsV2Store } from "@stores/survey/resultsV2/resultsV2";
  import { useExportQuestionOptions } from "@utils/composables/useExportQuestionOptions";
  import {
    defaultExportPayload,
    exportTypeFlags,
    exportTypeOf,
    payloadFrom,
    sanitizeExportPayload,
    useExportsStore,
  } from "@stores/survey/exports";
  import type { ExportFileFormat, ExportType, SurveyExportPayload } from "@/types";

  const route = useRoute();
  const router = useRouter();

  const { isAdmin } = storeToRefs(useCurrentUserStore());
  const { token: surveyToken, areWeightsEnabled, hasCodification, hasMailingAccess } = storeToRefs(useSurveyStore());
  const { currentTargets } = storeToRefs(useSurveyTargetsStore());
  const { localeOptions, analysisLocale } = storeToRefs(useSurveyLocalesStore());
  const resultsV2Store = useResultsV2Store();
  const { availableCards, savedPopulations } = storeToRefs(resultsV2Store);
  const exportsStore = useExportsStore();

  const exportToken = computed(() => route.params.exportToken as string | undefined);
  const isEdit = computed(() => !!exportToken.value);

  const form = ref<SurveyExportPayload>(defaultExportPayload());
  const loaded = ref(false);
  const submitting = ref(false);
  const openSection = ref("general");
  const exportType = ref<ExportType>("individual");
  const individualFormat = ref<ExportFileFormat>("xlsx");
  const autoName = `export_${Date.now()}`;

  const flags = computed(() => exportTypeFlags(exportType.value, individualFormat.value));

  onMounted(async () => {
    const pending: Promise<unknown>[] = [];
    if (!availableCards.value.length) pending.push(resultsV2Store.fetchAvailableCards());
    if (!savedPopulations.value.length) pending.push(resultsV2Store.fetchSavedPopulations().catch(() => {}));
    await Promise.all(pending);
    await seedForm();
    loaded.value = true;
  });
  // Export-to-export navigation reuses the component instance: onMounted won't re-run.
  watch(exportToken, () => seedForm());

  async function seedForm() {
    if (exportToken.value) {
      const existing = exportsStore.findByToken(exportToken.value) ?? (await exportsStore.fetchExport(exportToken.value));
      if (!existing) return backToList();
      form.value = payloadFrom(existing);
    } else {
      form.value = freshPayload();
    }
    exportType.value = exportTypeOf(form.value.file_format);
    if (exportType.value === "individual") individualFormat.value = form.value.file_format;
  }

  const freshPayload = (): SurveyExportPayload => ({
    ...defaultExportPayload(),
    locale: analysisLocale.value?.token ?? null,
    weighted_data: areWeightsEnabled.value,
  });

  // Hidden fields would otherwise keep stale values and shape the export.
  const resetOptions = () => (form.value = { ...freshPayload(), name: form.value.name });
  const changeType = (value: ExportType) => {
    exportType.value = value;
    resetOptions();
  };
  const changeFormat = (value: ExportFileFormat) => {
    individualFormat.value = value;
    resetOptions();
  };
  // Admin-only cards may sit in any question option: drop them on a mode switch.
  const changeAdminMode = (value: boolean) => {
    form.value.admin = isAdmin.value ? value : false;
    form.value.mode = "expand";
    form.value.question_names = "";
    form.value.crosstab_questions = [];
    form.value.custom_bases = {};
  };

  const adminMode = computed(() => form.value.admin);
  const { closedQuestionOptions, baseQuestionOptions, questionNameOptions } = useExportQuestionOptions(adminMode);

  const hasMultipleLocales = computed(() => localeOptions.value.length > 1);
  const targetOptions = computed(() => [
    { value: "all", label: "All targets" },
    ...currentTargets.value.map((target: { token: string; name: string }) => ({ value: target.token, label: target.name })),
  ]);
  const savedPopulationOptions = computed(() =>
    savedPopulations.value.map((group: { token: string; name: string }) => ({ value: group.token, label: group.name })),
  );
  const populationOptions = computed(() => [{ value: "all", label: "All respondents" }, ...savedPopulationOptions.value]);
  const verbatimFormatOptions = [
    { value: "translated", label: "Translated" },
    { value: "raw", label: "Raw" },
  ];
  const aiChatInterviewStatusOptions = [
    { value: "valid", label: "Valid" },
    { value: "rejected", label: "Rejected (Low Quality)" },
    { value: "abandoned", label: "Abandoned (Dropouts)" },
    { value: "disqualified", label: "Disqualified (Screened Out without Low Quality)" },
  ];

  const population = computed<string>({
    get: () => form.value.population || "all",
    set: (value) => (form.value.population = value === "all" ? null : value),
  });

  const backToList = () => router.push({ name: "SurveyExports", params: { token: surveyToken.value } });

  async function handleGenerate() {
    submitting.value = true;
    try {
      const payload = sanitizeExportPayload(form.value, {
        flags: flags.value,
        isAdmin: isAdmin.value,
        weightsEnabled: areWeightsEnabled.value,
        fallbackName: autoName,
      });
      const saved = exportToken.value
        ? await exportsStore.updateExport(exportToken.value, payload)
        : await exportsStore.createExport(payload);
      // Not awaited: the toaster handles progress and download.
      exportsStore.generateExport(saved.token);
      backToList();
    } finally {
      submitting.value = false;
    }
  }
</script>

<template>
  <ThreePanePage
    :span-left="2"
    :span-center="8"
    :span-right="2"
    data-cy="survey-export-form-page">
    <template #center>
      <Card class="p-6">
        <CardHeader>
          <CardTitle class="text-2xl">{{ isEdit ? "Edit export" : "New export" }}</CardTitle>
        </CardHeader>
        <CardContent>
          <div
            v-if="!loaded"
            class="flex items-center justify-center py-12">
            <Loader2 class="h-6 w-6 animate-spin text-muted-foreground" />
          </div>

          <template v-else>
            <Accordion
              v-model="openSection"
              type="single"
              collapsible>
              <ExportFormGeneral
                v-model:form="form"
                :export-type="exportType"
                :individual-format="individualFormat"
                :show-mode="isAdmin && (flags.isExcel || flags.isTables)"
                :show-format="flags.isIndividual"
                :weights-enabled="areWeightsEnabled"
                :auto-name="autoName"
                @change-type="changeType"
                @change-format="changeFormat"
                @change-admin-mode="changeAdminMode" />

              <AccordionItem
                v-if="flags.isAiChat"
                value="ai_chat">
                <AccordionTrigger class="text-base font-semibold">AI conversations</AccordionTrigger>
                <AccordionContent>
                  <div class="space-y-6 pt-2">
                    <div class="space-y-2">
                      <Label>Translation</Label>
                      <Select
                        v-model="form.ai_chat_translation"
                        :options="verbatimFormatOptions"
                        :disabled="!hasMultipleLocales"
                        class="w-full" />
                    </div>
                    <div
                      v-if="isAdmin"
                      class="space-y-2">
                      <Label>Interview Status</Label>
                      <Select
                        v-model="form.ai_chat_interview_status"
                        :options="aiChatInterviewStatusOptions"
                        multiple
                        class="w-full" />
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem
                v-if="!flags.isRewards"
                value="filter">
                <AccordionTrigger class="text-base font-semibold">Filter</AccordionTrigger>
                <AccordionContent>
                  <div class="space-y-6 pt-2">
                    <div class="space-y-2">
                      <Label>Target</Label>
                      <Select
                        v-model="form.target_token"
                        :options="targetOptions"
                        class="w-full" />
                    </div>
                    <div class="space-y-2">
                      <Label>Population</Label>
                      <Select
                        v-model="population"
                        :options="populationOptions"
                        class="w-full" />
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>

              <ExportFormIndividual
                v-if="flags.isIndividual"
                v-model:form="form"
                :question-name-options="questionNameOptions"
                :show-codification="hasCodification"
                :show-email="hasMailingAccess || isAdmin"
                :show-admin-fields="isAdmin && flags.isExcel"
                :show-display="flags.isSpreadsheet"
                :is-excel="flags.isExcel"
                :is-admin="isAdmin" />

              <ExportFormTables
                v-if="flags.isTables"
                v-model:form="form"
                :closed-question-options="closedQuestionOptions"
                :base-question-options="baseQuestionOptions"
                :population-options="savedPopulationOptions" />

              <AccordionItem
                v-if="!flags.isAiChat && !flags.isRewards"
                value="language">
                <AccordionTrigger class="text-base font-semibold">Language</AccordionTrigger>
                <AccordionContent>
                  <div class="space-y-6 pt-2">
                    <div class="space-y-2">
                      <Label>Display language</Label>
                      <Select
                        v-model="form.locale"
                        :options="localeOptions"
                        clearable
                        class="w-full" />
                    </div>
                    <div
                      v-if="flags.isSpreadsheet"
                      class="space-y-2">
                      <Label>Verbatims language</Label>
                      <Select
                        v-model="form.verbatim_format"
                        :options="verbatimFormatOptions"
                        class="w-full" />
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>

            <div class="flex items-center justify-end gap-2 pt-6">
              <Button
                variant="outline"
                @click="backToList">
                Cancel
              </Button>
              <Button
                :disabled="submitting"
                @click="handleGenerate">
                <Loader2
                  v-if="submitting"
                  class="mr-2 size-4 animate-spin" />
                <Download
                  v-else
                  class="mr-2 size-4" />
                Generate export
              </Button>
            </div>
          </template>
        </CardContent>
      </Card>
    </template>
  </ThreePanePage>
</template>
